import React from 'react';
import { Trans } from 'react-i18next';

import {
    ChatDescriptionSkeleton,
    ChatSummarySkeleton,
} from 'flow/components/loaders/ChatSummarySkeleton';
import BaseModal, { modalSizes } from 'flow/components/ui-components/BaseModal';
import ChatAvatar from 'flow/components/ui-components/ChatAvatar';
import { useModal } from 'flow/contexts/ModalProvider';
import { useSummarizeChatMessages } from 'flow/hooks/api/useChats';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { SummaryOption } from 'flow/types/SummarizeMessage';
import { sanitizeHTML } from 'flow/utils/app-utils';
import { dateFormatter } from 'flow/utils/date-time-utils';

interface Props {
    chatSessionKey?: ChatSessionKey;
    summaryOption?: SummaryOption;
}

const ChatSummaryModal = ({ chatSessionKey, summaryOption }: Props) => {
    const { i18next } = useAppContext();
    const { closeModal } = useModal();

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', chatSessionKey]),
    );

    return (
        <BaseModal
            open={true}
            header={i18next.t('chat.summary.title')}
            modalSize={modalSizes.MEDIUM}
            hideCancelButton={true}
            onSubmit={() => {}}
            onClose={closeModal}
            confirmButtonText={i18next.t('modal.close')}
        >
            <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <ChatAvatar session={session} showUnreadBadge={false} />
                        <strong>{session.get('name')}</strong>
                    </div>
                    <Description
                        chatSessionKey={chatSessionKey}
                        summaryOption={summaryOption}
                    />
                </div>

                <Content
                    chatSessionKey={chatSessionKey}
                    summaryOption={summaryOption}
                />
            </div>
        </BaseModal>
    );
};

export default ChatSummaryModal;

const Description = ({ chatSessionKey, summaryOption }: Props) => {
    const { i18next } = useAppContext();

    const { data, isFetching, isError } = useSummarizeChatMessages(
        chatSessionKey,
        summaryOption,
    );

    if (isFetching) {
        return <ChatDescriptionSkeleton />;
    }

    if (isError) {
        return null;
    }

    return (
        <p className="text-sm text-gray-500">
            <Trans
                i18n={i18next}
                i18nKey="chat.summary.description"
                values={{
                    startAt: dateFormatter(data.summarizeStartAt),
                    endAt: dateFormatter(data.summarizeEndAt),
                    messages: data.numberOfMessagesSummarized,
                }}
                components={[<strong key="0" />]}
            />
        </p>
    );
};

const Content = ({ chatSessionKey, summaryOption }: Props) => {
    const { i18next } = useAppContext();

    const { data, error, isError, isFetching } = useSummarizeChatMessages(
        chatSessionKey,
        summaryOption,
    );

    if (isFetching) {
        return <ChatSummarySkeleton />;
    }

    if (isError) {
        const errorSummary = error.response
            ? JSON.parse(error.response)?.message
            : 'parameters_are_missing';
        return (
            <p className="text-sm text-red-500">
                {i18next.t(`chat.summary.errors.${errorSummary}`)}
            </p>
        );
    }

    return (
        <>
            <div
                className="prose prose-sm max-w-none [&_b]:font-semibold [&_li]:mb-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-6"
                dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(data.summary),
                }}
            />

            <p className="text-sm text-gray-500">
                {i18next.t('chat.summary.note')}
            </p>
        </>
    );
};
